import { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
// The admin area (and its drag-and-drop dependencies) is only needed by
// administrators, so keep it out of the initial bundle.
const AdminPage = lazy(() => import('./pages/AdminPage'));
const HelpdeskPage = lazy(() => import('./pages/HelpdeskPage'));
const GuestTicketPage = lazy(() =>
    import('./pages/HelpdeskPage').then((module) => ({ default: module.GuestTicketPage }))
);
import { useGetProfileQuery, useLazySilentLoginQuery } from './store/apiSlice';

const App = () => {
    const guestPage = window.location.pathname === '/helpdesk/guest';
    const {
        data: user,
        isLoading: userLoading,
        error: userError,
        refetch: refetchUser,
    } = useGetProfileQuery(undefined, { skip: guestPage });

    const [triggerSilentLogin] = useLazySilentLoginQuery();

    const isUnauthorized =
        !!userError &&
        typeof userError === 'object' &&
        'status' in userError &&
        (userError as any).status === 401;

    const attemptedAutoLoginRef = useRef(false);

    // Without a local session, go straight to the IdP: while its session lasts that needs no
    // password, and the IdP re-checks the person in ChurchTools. Not right after a logout.
    useEffect(() => {
        if (attemptedAutoLoginRef.current) return;
        if (guestPage) return;
        // Never auto-login from inside the silent-login iframe. If /oidc is ever
        // misrouted to the SPA fallback (e.g. mod_rewrite unavailable), the nested
        // app would otherwise spawn another iframe and recurse without bound.
        if (window.top !== window.self) return;
        // The login page owns interactive retries; errors/logout must not trigger another SSO loop.
        if (window.location.pathname === '/login') return;
        if (userLoading) return;
        if (user) return;
        if (!isUnauthorized) return;

        attemptedAutoLoginRef.current = true;

        let canceled = false;
        (async () => {
            try {
                const apiResult = await triggerSilentLogin().unwrap();
                if (canceled || apiResult?.autoLoginBlocked) return;

                if (apiResult?.authenticated) {
                    await refetchUser();
                    return;
                }

                // (A hidden prompt=none iframe cannot work here: the IdP forbids being framed.)
                const redirectPath = window.location.pathname + window.location.search;
                window.location.href = `/oidc?redirect=${encodeURIComponent(redirectPath)}`;
            } catch {
                // ignore
            }
        })();

        return () => {
            canceled = true;
        };
    }, [user, userLoading, isUnauthorized, triggerSilentLogin, refetchUser, guestPage]);

    if (userLoading && !userError) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Suspense
            fallback={
                <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                    <CircularProgress />
                </Stack>
            }
        >
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/helpdesk" element={<HelpdeskPage />} />
                <Route path="/helpdesk/guest" element={<GuestTicketPage />} />
                <Route path="/admin/helpdesk" element={<HelpdeskPage admin />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
};

export default App;
